/* eslint-disable no-magic-numbers */
import { HttpStatusCode } from 'axios';
import { HttpResponse, delay, http } from 'msw';

import {
	associatedForms,
	createdForm,
	form,
	forms,
	formsWithArchived,
	interviewTemplates,
	question,
	questions,
	recommendationGroup,
} from './mockData';

const {
	feedbackApi: { baseUrl },
	mockDelay,
} = global.config;

const convertTypeReverse = (type) =>
	({
		'Choice Dropdown Single': 'choice',
		'Text Field': 'textinput',
		'Choice Dropdown Multiple': 'choice',
		'Text Area': 'textarea',
		'Choice Radio': 'choice',
		'Choice Checkbox': 'choice',
		Rating: 'number',
		Paragraph: 'paragraph',
	})[type] ?? 'paragraph';

const getChoiceField = (type) =>
	({
		'Choice Dropdown Single': 'dropdown',
		'Choice Radio': 'radio',
		'Choice Checkbox': 'checkbox',
	})[type] ?? null;

const formHandlers = [
	http.get(`${baseUrl}/proxy/feedback/biz-collab-teams/forms`, async () => {
		await delay(mockDelay);

		return HttpResponse.json(associatedForms, { status: HttpStatusCode.Ok });
	}),

	http.get(`${baseUrl}/proxy/feedback/v2/biz-collab-teams/forms`, async ({ request }) => {
		await delay(mockDelay);

		const url = new URL(request.url);
		const name = url.searchParams.get('name');

		if (name === 'error') {
			return HttpResponse.error();
		}

		if (request.url.includes('includeArchived=true')) {
			return HttpResponse.json(
				{
					...formsWithArchived,
					items: formsWithArchived.items.slice(0, 50),
				},
				{ status: HttpStatusCode.Ok },
			);
		}
		return HttpResponse.json(
			{
				...forms,
				items: forms.items.slice(0, 50),
			},
			{ status: HttpStatusCode.Ok },
		);
	}),

	http.get(`${baseUrl}/proxy/feedback/form/:id`, async () => {
		await delay(mockDelay);

		return HttpResponse.json({ form }, { status: HttpStatusCode.Ok });
	}),

	http.get(`${baseUrl}/proxy/feedback/biz-collab-teams/forms/:id`, async () => {
		await delay(mockDelay);

		return HttpResponse.json(form, { status: HttpStatusCode.Ok });
	}),

	http.post(`${baseUrl}/proxy/feedback/v2/biz-collab-teams/forms`, async ({ request }) => {
		await delay(mockDelay);

		const body = await request.json();

		if (body.formNames[body.defaultLanguage] === 'error') {
			return HttpResponse.error();
		}

		if (body.formNames[body.defaultLanguage] === 'duplicate form') {
			return HttpResponse.json('Conflict', { status: HttpStatusCode.Conflict });
		}

		return HttpResponse.json(createdForm);
	}),

	http.patch(`${baseUrl}/proxy/feedback/v2/biz-collab-teams/forms/:id`, async () => {
		await delay(mockDelay);

		return HttpResponse.json(createdForm);
	}),

	http.get(`${baseUrl}/proxy/feedback/biz-collab-teams/recommendationGroup`, async () => {
		await delay(mockDelay);

		return HttpResponse.json(recommendationGroup, { status: HttpStatusCode.Ok });
	}),
];

const questionHandlers = [
	http.get(`${baseUrl}/proxy/feedback/v2/biz-collab-teams/questions`, async ({ request }) => {
		await delay(mockDelay);

		const url = new URL(request.url);

		const search = url.searchParams.get('search');
		const types = url.searchParams.get('type');
		const excludeArchived = url.searchParams.get('excludeArchived');
		const locales = url.searchParams.get('locale');
		const ids = url.searchParams.get('ids');

		const questionsFiltered = { ...questions };

		if (search === 'error') {
			return HttpResponse.error();
		}

		if (search !== null) {
			questionsFiltered.items = questionsFiltered.items.filter((item) =>
				item.content.fieldName.includes(search),
			);
		}

		if (excludeArchived === 'all') {
			questionsFiltered.items = questionsFiltered.items.filter((item) => !item.content.archived);
		}

		if (locales !== null) {
			const localesArray = locales.split(',');
			questionsFiltered.items = questionsFiltered.items.filter((item) => {
				for (const locale of localesArray) {
					if (Object.hasOwn(item.content.label, locale)) {
						return true;
					}
				}
				return false;
			});
		}

		if (types !== null) {
			const typesArray = types.split(',');
			questionsFiltered.items = questionsFiltered.items.filter((item) => {
				if (typesArray.includes('SELECT') && item.fieldType === 'choice') {
					return true;
				}
				if (
					typesArray.includes('TEXTAREA') &&
					['textinput', 'textarea', 'paragraph'].includes(item.fieldType)
				) {
					return true;
				}
				if (
					typesArray.includes('NUMBER') &&
					item.content.isRating !== null &&
					item.content.isRating
				) {
					return true;
				}
				return typesArray.includes(item.fieldType);
			});
		}

		if (ids !== null) {
			questionsFiltered.items = questions.items.filter((item) => item.key === ids);
			if (questionsFiltered.items.length === 0) {
				questionsFiltered.items = [question];
			}
		}

		return HttpResponse.json(
			{
				...questionsFiltered,
				items: questionsFiltered.items.slice(0, 50),
			},
			{ status: HttpStatusCode.Ok },
		);
	}),

	http.get(`${baseUrl}/proxy/feedback/question/:id`, async () => {
		await delay(mockDelay);

		return HttpResponse.json(question, { status: HttpStatusCode.Ok });
	}),

	http.post(`${baseUrl}/proxy/feedback/v2/biz-collab-teams/questions`, async ({ request }) => {
		await delay(mockDelay);

		const body = await request.json();

		if (body.fieldName === 'error') {
			return HttpResponse.error();
		}

		if (body.fieldName === 'duplicate question') {
			return HttpResponse.json('Conflict', { status: HttpStatusCode.Conflict });
		}

		return HttpResponse.json({
			formBuilderPart: JSON.stringify({
				key: '23233',
				fieldType: convertTypeReverse(body.type),
				content: {
					archived: false,
					fieldName: body.fieldName,
					label: body.title,
					choice: getChoiceField(body.type),
					choices: body.options,
				},
			}),
		});
	}),

	http.patch(`${baseUrl}/proxy/feedback/v2/biz-collab-teams/questions`, async ({ request }) => {
		await delay(mockDelay);

		const body = await request.json();

		if (body.questionId === 'error') {
			return HttpResponse.error();
		}

		return HttpResponse.json({
			formBuilderPart: JSON.stringify({
				key: body.questionId,
				fieldType: convertTypeReverse(body.type),
				content: {
					archived: false,
					fieldName: body.fieldName,
					label: body.title,
					choice: getChoiceField(body.type),
					choices: body.options,
				},
			}),
		});
	}),

	http.get(`${baseUrl}/proxy/feedback/interview/templates`, async ({ request }) => {
		await delay(mockDelay);

		const url = new URL(request.url);
		const feedbackFormId = url.searchParams.get('feedbackFormId');

		// Only return templates for form with ID 61
		if (feedbackFormId === '61') {
			return HttpResponse.json(interviewTemplates);
		}

		return HttpResponse.json([]);
	}),
];

const handlers = [...formHandlers, ...questionHandlers];

export { handlers };
